import React, { useState } from "react";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import type { RecordingSession } from "../../../lib/recordingStore";

interface RecoveredRecordingsProps {
  sessions: RecordingSession[];
  /** True while a recording is live, a clip is waiting or a job is being submitted. */
  transcribeDisabled: boolean;
  /** The recording whose upload is under way: its Transcribe it and Discard are disabled. */
  busySessionId?: string | null;
  error: string;
  onTranscribe: (session: RecordingSession) => void;
  onDownload: (session: RecordingSession) => void;
  onDiscard: (session: RecordingSession) => void;
}

export const formatRecordedAt = (startedAt: number): string =>
  new Date(startedAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });

export const formatClockDuration = (durationMs: number): string => {
  const total = Math.max(0, Math.round(durationMs / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/** Non-blocking notices for recordings saved in this browser that were never processed. */
const RecoveredRecordings: React.FC<RecoveredRecordingsProps> = ({
  sessions,
  transcribeDisabled,
  busySessionId = null,
  error,
  onTranscribe,
  onDownload,
  onDiscard,
}) => {
  const [discardTarget, setDiscardTarget] = useState<RecordingSession | null>(null);

  if (sessions.length === 0 && !error) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
      {sessions.map((session) => (
        <Alert
          key={session.id}
          severity="warning"
          action={
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button color="inherit" size="small" disabled={transcribeDisabled || session.id === busySessionId} onClick={() => onTranscribe(session)}>
                Transcribe it
              </Button>
              <Button color="inherit" size="small" onClick={() => onDownload(session)}>
                Download
              </Button>
              <Button
                color="inherit"
                size="small"
                disabled={session.id === busySessionId}
                onClick={() => setDiscardTarget(session)}
              >
                Discard
              </Button>
            </Box>
          }
        >
          {`We found a recording from ${formatRecordedAt(session.startedAt)} (${formatClockDuration(session.durationMs)}) that wasn't processed.${session.incomplete ? " This recording may be incomplete." : ""}`}
        </Alert>
      ))}
      {error && <Alert severity="error">{error}</Alert>}

      <Dialog open={discardTarget !== null} onClose={() => setDiscardTarget(null)}>
        <DialogTitle>Discard this recording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The recording saved in this browser will be deleted and cannot be recovered afterwards.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardTarget(null)}>Keep it</Button>
          <Button
            color="error"
            onClick={() => {
              if (discardTarget) onDiscard(discardTarget);
              setDiscardTarget(null);
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecoveredRecordings;
